import React, { useState, useMemo, useEffect, type Dispatch, type SetStateAction } from 'react';
import type { BOMType, FilterType, ProductData } from '../../type';
import { configOrderIndex, formatConfigValue } from '../../lib/productConfigFormat';
import { prettyLabel } from '../../lib/prettyLabel';
import { ConfigRow } from './ConfigRow';
import { OptionButton } from './OptionButton';
import { MetalButton } from './MetalButton';
import { METAL_OPTIONS } from './constants';
import { Button } from '../../components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle
} from '@univdiam/ui/components/dialog';
import { Input } from '@univdiam/ui/components/input';
import { toast } from 'sonner';
import { Label } from '@univdiam/ui/components/label';
import { Sparkles } from 'lucide-react';

const SparkleIcon = () => (
  <div className="w-9 h-9 rounded-md bg-slate-50 border border-slate-100 flex items-center justify-center">
    <Sparkles className="h-5 w-5 text-slate-700" />
  </div>
);

type ProductConfigType = {
  filters: ProductData;
  filterdValue?: FilterType;
  setFilterdValue?: Dispatch<SetStateAction<FilterType | null>>;
  isCad?: boolean;
  handleCatalogCreateOrder?: () => void;
  shareProductTouser?: string;
  isValidSelection?: () => string | true | undefined;
  isPending?: boolean;
  price?: string;
  customerPrice?: string;
  setCustomerPrice: Dispatch<SetStateAction<string>>;
  currentCAD?: any;
  isAdmin?: boolean;
  createCADOrder?: (data: any) => void;
  instruction?: string;
  setInstrcution?: Dispatch<SetStateAction<string>>;
  submittedOrders?: any[];
  onGenerateLink?: (includePrice?: boolean) => void;
  isGeneratingLink?: boolean;
  selectedProductForCAD: BOMType | null | undefined
};

const ProductConfigForCAD: React.FC<ProductConfigType> = ({
  setFilterdValue,
  shareProductTouser,
  isValidSelection,
  currentCAD,
  customerPrice,
  setCustomerPrice,
  createCADOrder,
  instruction,
  setInstrcution,
  submittedOrders,
  onGenerateLink,
  isGeneratingLink,
  selectedProductForCAD
}) => {
  const [selectedAttributes, setSelectedAttributes] = useState<Record<string, string>>({});
  const [openModal, setOpenModal] = useState(false);
  const [copied, setCopied] = useState(false);

  // Collect attribute options from CAD BOM
  const attributeOptions = useMemo(() => {
    if (!currentCAD?.bom || !Array.isArray(currentCAD.bom)) return {};
    const options: Record<string, string[]> = {};
    currentCAD.bom.forEach((item: any) => {
      Object.entries(item).forEach(([key, value]) => {
        if (!options[key]) options[key] = [];
        if (value && !options[key].includes(String(value))) {
          options[key].push(String(value));
        }
      });
    });
    return options;
  }, [currentCAD]);

  // Attribute keys (skip id + price always)
  const attributeKeys = useMemo(() => {
    return Object.keys(attributeOptions).filter((k) => k !== 'id' && k !== 'price');
  }, [attributeOptions]);

  // Handle select
  const handleSelect = (key: string, value: string) => {
    setSelectedAttributes((prev) => ({ ...prev, [key]: value }));
  };

  // Sync with parent
  useEffect(() => {
    if (setFilterdValue) setFilterdValue(selectedAttributes as unknown as FilterType);
  }, [selectedAttributes]);

  // Auto-select first available options
  useEffect(() => {
    if (Object.keys(attributeOptions).length === 0) return;

    setSelectedAttributes((prev) => {
      const next = { ...prev };
      let hasChanges = false;

      attributeKeys.forEach((key) => {
        if (!next[key] && attributeOptions[key] && attributeOptions[key].length > 0) {
          next[key] = attributeOptions[key]![0]!;
          hasChanges = true;
        }
      });

      return hasChanges ? next : prev;
    });
  }, [attributeOptions, attributeKeys]);

  const handleCopy = async () => {
    if (!shareProductTouser) return;
    await navigator.clipboard.writeText(shareProductTouser);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Metal Type renders on its own above; the rest follow the shared display
  // order (Diamond Quality -> Carat Weight -> anything else).
  const otherAttributeKeys = useMemo(
    () =>
      attributeKeys
        .filter((attr) => attr !== 'metaltype')
        .sort((a, b) => configOrderIndex(a) - configOrderIndex(b)),
    [attributeKeys]
  );

  const filteredMetals = METAL_OPTIONS.filter(metal =>
    attributeOptions?.metaltype?.includes(metal.id2)
  );

  // Share + order need a saved CAD design to point at. The admin discussion view
  // renders a product that has not been sent to the customer yet — no design id,
  // nothing to share or order.
  const hasCadDesign = currentCAD?.id != null;

  // One order request per variant: same CAD design + same BOM variant can't be
  // submitted twice. Other variants of the same design stay orderable.
  const variantId = (selectedProductForCAD as any)?.id;
  const alreadySubmitted = variantId != null && !!submittedOrders?.some((cad) => {
    if (String(cad.cad_design_id) !== String(currentCAD?.id)) return false;
    // bom is stored JSON-stringified, sometimes double-encoded — unwrap up to twice.
    let bom: any = cad.bom;
    for (let i = 0; i < 2 && typeof bom === 'string'; i++) {
      try { bom = JSON.parse(bom); } catch { return false; }
    }
    return bom?.id != null && String(bom.id) === String(variantId);
  });

  return (
    <div>
      <div className="w-full bg-white rounded-xl border border-slate-200 overflow-hidden">
        {/* Header - Made slightly softer with an off-white bg */}
        <header className="flex items-center space-x-3 p-4 border-b border-slate-100">
          <SparkleIcon />
          <h1 className="text-body1 text-slate-900 tracking-tight">Product Configuration</h1>
        </header>

        <div className="px-6 py-6 flex flex-col gap-8">
          {/* Metal Type special rendering */}
          {attributeKeys.includes('metaltype') && (
            <ConfigRow
              key="metaltype"
              label="Metal Type"
              value={selectedAttributes['metaltype'] || ''}
            >
              <div className="flex flex-wrap gap-2">
                {filteredMetals.map((metal) => (
                  <MetalButton
                    key={metal.id}
                    label={metal.karat}
                    colorClass={metal.colorClass}
                    textColorClass={metal.textColorClass}
                    isSelected={selectedAttributes['metaltype'] === metal.id2}
                    onClick={() => handleSelect('metaltype', metal.id2)}
                  />
                ))}
              </div>
            </ConfigRow>
          )}

          {/* Render all other attributes */}
          {otherAttributeKeys.map((attr) => (
            <ConfigRow
              key={attr}
              label={prettyLabel(attr)}
              value={formatConfigValue(attr, selectedAttributes[attr] || '')}
            >
              {attributeOptions[attr]?.map((option) => (
                <OptionButton
                  key={option}
                  label={formatConfigValue(attr, option)}
                  isSelected={selectedAttributes[attr] === option}
                  onClick={() => handleSelect(attr, option)}
                />
              ))}
            </ConfigRow>
          ))}

          {/* Special Instructions - Cleaned up label and standard focus states */}
          {hasCadDesign && (
            <div className="space-y-2">
              <label htmlFor="special-instructions" className="block text-sm font-semibold text-slate-800">
                Special Instructions
              </label>
              <textarea
                id="special-instructions"
                value={instruction}
                onChange={(e) => setInstrcution && setInstrcution(e.target.value)}
                className="w-full min-h-[100px] p-4 text-sm border border-slate-200 rounded-sm placeholder:text-slate-400 focus:bg-white focus:ring-0.5 focus:ring-slate-300 focus:border-slate-300 outline-none transition-all resize-y"
                placeholder="Enter any special requests or notes for the jeweler here..."
              ></textarea>
            </div>
          )}

          {/* Action Bar - Fixed spacing and button conflicts */}
          {hasCadDesign && (
            <div className="flex flex-col-reverse sm:flex-row items-center gap-3 pt-6 mt-2 border-t border-slate-100">
              <Button
                variant="outline"
                type="button"
                className="w-full sm:w-auto h-11 px-6 font-medium"
                onClick={() => {
                  // if (submittedOrders && Array.isArray(submittedOrders)) {
                  //   if (submittedOrders.find((cad: any) => cad.cad_design_id === currentCAD.id)) {
                  //     toast.error('Order already submitted', { position: 'top-center' });
                  //     return;
                  //   }
                  // }
                  setOpenModal(true);
                }}
              >
                Share to Customer
              </Button>

              {!alreadySubmitted && (
                <Button
                  type="button"
                  className="w-full sm:w-auto sm:flex-1 h-11 font-medium"
                  onClick={() => {
                    if (!isValidSelection || typeof isValidSelection !== 'function' || !isValidSelection()) {
                      toast.error('Please select all required fields', { position: 'top-center' });
                      return;
                    }
                    if (createCADOrder) {
                      createCADOrder(currentCAD);
                    }
                  }}
                >
                  Submit Order Request
                </Button>
              )}
            </div>
          )}

          {/* Modal - Cleaned up spacing and layout */}
          <Dialog open={openModal} onOpenChange={setOpenModal}>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle className="text-xl font-semibold">Share this product</DialogTitle>
                <DialogDescription className="text-slate-500 mt-1.5">
                  Share the configuration link with your customer, with or without pricing.
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-slate-700">Set your price</Label>
                  <Input
                    placeholder="1,234.00"
                    value={customerPrice}
                    onChange={(e) => {
                      let value = e.target.value.replace(/[^0-9.]/g, "");

                      const parts = value.split(".");

                      if (parts.length > 2) {
                        value = parts[0] + "." + parts[1];
                      }

                      if (parts[1]) {
                        value = `${parts[0]}.${parts[1].slice(0, 2)}`;
                      }

                      setCustomerPrice(value);
                    }}
                    className="h-11"
                    type="text"
                    inputMode="decimal"
                  />
                </div>

                {!shareProductTouser && (
                  <div className="flex flex-col gap-2">
                    <Button
                      type="button"
                      className="w-full h-11 font-medium"
                      onClick={() => onGenerateLink && onGenerateLink(true)}
                      disabled={!customerPrice || isGeneratingLink}
                    >
                      {isGeneratingLink ? 'Generating...' : 'Generate Link'}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full h-11 font-medium"
                      onClick={() => onGenerateLink && onGenerateLink(false)}
                      disabled={isGeneratingLink}
                    >
                      Generate Link Without Price
                    </Button>
                  </div>
                )}

                {shareProductTouser && (
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-slate-700">Share Link</Label>
                    <div className="flex items-center gap-2">
                      <Input
                        value={shareProductTouser || ''}
                        readOnly
                        className="flex-1 h-11 bg-slate-50 text-slate-600 focus-visible:ring-0"
                      />
                      <Button onClick={handleCopy} className="h-11 px-6 min-w-[100px]">
                        {copied ? 'Copied!' : 'Copy'}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>
    </div>
  );
};

export default ProductConfigForCAD;